import React, { useRef } from "react";
import { motion } from "framer-motion";
import { fadeUp, slideLeft, slideRight } from "../../animations/variants";
import emailjs from "@emailjs/browser";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { useSelector } from "react-redux";

import { speakWithUsText } from "../../i18n/speakWithUs";

const SpeakWithUs = () => {
  const formRef = useRef();

  const lang = useSelector((state) => state.language.lang);
  const t = speakWithUsText[lang];

  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const navigate = useNavigate();

  const formatDatePretty = () => {
    const date = new Date();

    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;

    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    // Add ordinal suffix (st, nd, rd, th)
    const ordinal =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
        ? "rd"
        : "th";

    return `At ${hour12}:${minutes} ${ampm} on ${day}${ordinal} ${month} ${year}`;
  };

  const sendEmail = (e) => {
    e.preventDefault();

    toast.loading(t.toast.sending, { id: "sendMsg" });

    emailjs
      .sendForm(
        "service_f4xy38a", // 🔁 your EmailJS service ID
        "template_s50dmda", // 🔁 your template ID
        formRef.current,
        "avq_ofiaYk3SDzsuu" // 🔁 your public key
      )
      .then(
        (result) => {
          console.log(result.text);

          toast.success(t.toast.success, { id: "sendMsg" });

          formRef.current.reset();

          setTimeout(() => {
            navigate("/");
          }, 1500);
        },
        (error) => {
          console.log(error.text);

          toast.error(t.toast.error, { id: "sendMsg" });
        }
      );
  };

  return (
    <section className="bg-navy py-24 px-6 text-white h-screen">
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-y-10 mt-6 md:mt-16">
        {/* LEFT TITLE */}
        <motion.div
          variants={slideLeft}
          initial="hidden"
          whileInView="show"
          viewport={{ amount: 0.1 }}
          className="flex flex-col items-start justify-start"
        >
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-neonYellow animate-neonFlicker leading-tight mb-0">
            {t.title}
          </h1>
          {/* Title */}

          {/* Description */}
          <p className="hidden md:block text-gray-300 leading-relaxed mb-6 max-w-xl mx-auto pr-20 mt-4 md:mt-10 ">
            {t.description}
          </p>
        </motion.div>

        {/* RIGHT FORM */}
        <motion.form
          ref={formRef}
          onSubmit={sendEmail}
          variants={slideRight}
          initial="hidden"
          whileInView="show"
          viewport={{ amount: 0.1 }}
          className="space-y-3 md:space-y-6 text-gray-200 "
        >
          {/* NAME */}
          <motion.div variants={fadeUp}>
            <label className="text-sm font-medium text-yellow">
              {t.form.name} <span>(*)</span>
            </label>
            <div className="grid grid-cols-2 gap-4 mt-1">
              <input
                name="first_name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="py-3 px-4 rounded-lg bg-navy border border-white/20 text-white placeholder:text-textGray text-sm"
                placeholder={t.form.firstName}
                required
              />

              <input
                name="last_name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="py-3 px-4 rounded-lg bg-navy border border-white/20 text-white placeholder:text-textGray text-sm"
                placeholder={t.form.lastName}
                required
              />
            </div>

            {/* HIDDEN FULL NAME FIELD FOR EMAILJS */}
            <input
              type="hidden"
              name="name"
              value={`${firstName} ${lastName}`}
            />
            <input type="hidden" name="time" value={formatDatePretty()} />
          </motion.div>

          {/* EMAIL */}
          <motion.div variants={fadeUp}>
            <label className="text-sm font-medium text-yellow">
              Email <span className="text-yellow">(*)</span>
            </label>
            <input
              name="email"
              type="email"
              required
              className="w-full mt-1 py-3 px-4 rounded-lg bg-navy border border-white/20 text-white placeholder:text-textGray text-sm"
              placeholder="Your email"
            />
          </motion.div>

          {/* INTEREST DROPDOWN */}
          <motion.div variants={fadeUp}>
            <label className="text-sm font-medium text-yellow">
              {t.form.interest} <span>(*)</span>
            </label>

            <select
              name="title" // ← IMPORTANT CHANGE
              required
              className="w-full mt-1 py-3 px-4 rounded-lg bg-navy border border-white/20 text-textGray cursor-pointer text-sm max-w-[400px]"
            >
              <option value="">{t.form.selectOption}</option>
              <option value={t.form.option1}>{t.form.option1}</option>
              <option value={t.form.option2}>{t.form.option2}</option>
            </select>
          </motion.div>

          {/* MESSAGE */}
          <motion.div variants={fadeUp}>
            <label className="text-sm font-medium text-yellow">
              {t.form.message} <span>(*)</span>
            </label>
            <textarea
              name="message"
              rows="4"
              required
              className="w-full mt-1 py-3 px-4 rounded-lg bg-navy border border-white/20 text-white placeholder:text-textGray text-sm resize-none"
              placeholder={t.form.messagePlaceholder}
            />
          </motion.div>

          {/* BUTTON */}
          <motion.div
            variants={fadeUp}
            className="inline-block cursor-pointer select-none"
          >
            <button
              type="submit"
              className="heroneon px-8 py-3 text-lg font-semibold"
            >
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              {t.form.submit}
            </button>
          </motion.div>
        </motion.form>
      </div>
    </section>
  );
};

export default SpeakWithUs;
